/**
 * LatencyStats 组件
 *
 * 显示各供应商的延迟统计图表。
 */

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { SearchLatencyItem } from '../../../services/searchAdminApi';

interface LatencyStatsProps {
  data: SearchLatencyItem[];
  loading: boolean;
}

export const LatencyStats: React.FC<LatencyStatsProps> = ({
  data,
  loading,
}) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="h-48 animate-pulse bg-gray-100 rounded" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500">
        暂无延迟数据
      </div>
    );
  }

  const chartData = data.map((d) => ({
    name: `${d.provider_name} #${d.key_index}`,
    avg: Math.round(d.avg_latency_ms),
    last: Math.round(d.last_latency_ms),
    checks: d.total_checks,
  }));

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-600 mb-4">
          延迟统计 (ms)
        </h3>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10 }}
                stroke="#9ca3af"
              />
              <YAxis
                tick={{ fontSize: 10 }}
                stroke="#9ca3af"
                unit="ms"
              />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 4 }} />
              <Bar dataKey="avg" name="平均延迟" fill="#3b82f6" radius={[2, 2, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      entry.avg > 3000
                        ? '#ef4444'
                        : entry.avg > 1000
                          ? '#f59e0b'
                          : '#3b82f6'
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 font-medium text-gray-600">
                  供应商
                </th>
                <th className="text-right p-3 font-medium text-gray-600">
                  最新延迟
                </th>
                <th className="text-right p-3 font-medium text-gray-600">
                  平均延迟
                </th>
                <th className="text-right p-3 font-medium text-gray-600">
                  检查次数
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((d, idx) => (
                <tr
                  key={`${d.provider_name}-${d.key_index}`}
                  className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                >
                  <td className="p-3 font-medium">
                    {d.provider_name} #{d.key_index}
                  </td>
                  <td
                    className={`text-right p-3 ${
                      d.last_latency_ms > 3000
                        ? 'text-red-600'
                        : d.last_latency_ms > 1000
                          ? 'text-yellow-600'
                          : 'text-green-600'
                    }`}
                  >
                    {Math.round(d.last_latency_ms)}ms
                  </td>
                  <td className="text-right p-3">
                    {Math.round(d.avg_latency_ms)}ms
                  </td>
                  <td className="text-right p-3 text-gray-500">
                    {d.total_checks}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LatencyStats;
